<template>
  <div class="dash-shell">
    <TopBar
      @select-camp="onSelectCamp"
      @scope-change="onScopeChange"
    />

    <SummaryGrid :camp-period-name="selectedCampName" :org-unit-id="selectedOrgUnitId" />

    <InsightGrid
      :camp-period-name="selectedCampName"
      :org-unit-id="selectedOrgUnitId"
      :role="selectedScope.role"
      :scope="selectedScope"
      :selected-dept-id="selectedDept.id"
      :selected-group-id="selectedGroup.orgUnitId"
      @select-dept="onSelectDept"
      @select-group="onSelectGroup"
      @default-selection-ready="onDefaultSelectionReady"
    />

    <main class="dash-board-grid">
      <AdvisorPanel
        :camp-period-name="selectedCampName"
        :org-unit-id="advisorOrgUnitId"
        :selected-team-name="selectedTeamName"
        :auto-select-first="true"
        @select-teacher="onSelectTeacher"
      />

      <FamilyPanel
        :camp-period-name="selectedCampName"
        :selected-teacher-name="selectedTeacherName"
        :auto-select-first="true"
        @select-parent="onSelectParent"
      />

      <JourneyPanel
        :selected-advisor="model.selectedAdvisor"
        :selected-family="model.selectedFamily"
        :profile="model.profile"
        :family-signals="familySignals"
        :camp-period-name="selectedCampName"
        :selected-customer-id="selectedCustomerId"
        :selected-sale-id="selectedSaleId"
        :selected-teacher-name="selectedTeacherName"
        :selected-parent="selectedParent"
      />
    </main>

    <ChatPanel
      :camp-period-name="selectedCampName"
      :customer-id="selectedCustomerId"
      :sale-id="selectedSaleId"
      :parent-name="selectedParentName"
      :visible="chatVisible || Boolean(selectedCustomerId && selectedSaleId)"
      @close="chatVisible = false"
    />
  </div>
</template>

<script setup>
import { ref } from 'vue'
import TopBar from '../components/control-tower/TopBar.vue'
import SummaryGrid from '../components/control-tower/SummaryGrid.vue'
import InsightGrid from '../components/control-tower/InsightGrid.vue'
import AdvisorPanel from '../components/control-tower/AdvisorPanel.vue'
import FamilyPanel from '../components/control-tower/FamilyPanel.vue'
import JourneyPanel from '../components/control-tower/JourneyPanel.vue'
import ChatPanel from '../components/control-tower/ChatPanel.vue'
import { useControlTowerData } from '../components/control-tower/useControlTowerData'

const selectedCampName = ref('')
const selectedOrgUnitId = ref('')
const selectedScope = ref({
  orgUnitId: '',
  orgName: '',
  orgType: '',
  role: '',
})
const selectedDept = ref({})
const selectedGroup = ref({})
const selectedTeamName = ref('')
const advisorOrgUnitId = ref('')
const selectedTeacherName = ref('')
const selectedCustomerId = ref('')
const selectedSaleId = ref('')
const selectedParentName = ref('')
const selectedParent = ref(null)
const chatVisible = ref(false)

const {
  model,
  familySignals,
} = useControlTowerData()

function onSelectCamp(camp) {
  selectedCampName.value = camp.name
  resetSelectionState()
}

function onSelectDept(dept) {
  selectedDept.value = dept || {}
  selectedGroup.value = {}
  selectedTeamName.value = ''
  advisorOrgUnitId.value = selectedScope.value.role === 'DEPT_LEAD'
    ? selectedScope.value.orgUnitId || selectedOrgUnitId.value
    : ''
  resetLowerSelection()
}

function onSelectGroup(group) {
  selectedGroup.value = group || {}
  selectedTeamName.value = group.teamName
  advisorOrgUnitId.value = group.orgUnitId || ''
  resetLowerSelection()
}

function onDefaultSelectionReady(selection) {
  if (selection?.dept) {
    selectedDept.value = selection.dept
  }
  if (selection?.group) {
    onSelectGroup(selection.group)
    return
  }
  if (selectedScope.value.role === 'MANAGER') {
    advisorOrgUnitId.value = selectedScope.value.orgUnitId || selectedOrgUnitId.value
    selectedTeamName.value = selectedScope.value.orgName || ''
  }
}

function onScopeChange(scope) {
  selectedScope.value = {
    orgUnitId: scope.orgUnitId || '',
    orgName: scope.orgName || '',
    orgType: scope.orgType || '',
    role: scope.role || '',
  }
  selectedOrgUnitId.value = scope.orgUnitId || ''
  resetSelectionState()
}

function resetLowerSelection() {
  selectedTeacherName.value = ''
  selectedCustomerId.value = ''
  selectedSaleId.value = ''
  selectedParentName.value = ''
  selectedParent.value = null
}

function onSelectTeacher(teacherName) {
  selectedTeacherName.value = teacherName
  resetParentSelection()
}

function onSelectParent(parent, options = {}) {
  selectedCustomerId.value = parent.customerId
  selectedSaleId.value = parent.saleId
  selectedParentName.value = parent.parentName
  selectedParent.value = parent
  if (!options.auto) {
    chatVisible.value = true
  }
}

function resetSelectionState() {
  selectedDept.value = {}
  selectedGroup.value = {}
  selectedTeamName.value = ''
  advisorOrgUnitId.value = selectedScope.value.role === 'MANAGER'
    ? selectedOrgUnitId.value
    : ''
  resetLowerSelection()
}

function resetParentSelection() {
  selectedCustomerId.value = ''
  selectedSaleId.value = ''
  selectedParentName.value = ''
  selectedParent.value = null
}
</script>

<style>
.dash-shell {
  max-width: 1500px;
  margin: 0 auto;
  padding: 16px 16px 24px;
  --dash-main-columns: minmax(300px, 0.92fr) minmax(320px, 1fr) minmax(340px, 1fr);
  --dash-title-size: 1.08rem;
  --dash-section-title-size: 1.02rem;
  --dash-copy-size: 0.8rem;
  --dash-caption-size: 0.66rem;
  --dash-kpi-size: 1.2rem;
  --dash-data-size: 0.78rem;
}

.dash-topbar,
.dash-summary-card,
.dash-group-card,
.dash-card,
.dash-panel,
.dash-focus-card,
.dash-coach-card,
.dash-timeline-item,
.dash-family-card,
.dash-classification-card,
.dash-action-item,
.dash-stage-row {
  border: 1px solid rgba(29, 47, 44, 0.08);
  background: var(--surface);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow);
  backdrop-filter: blur(12px);
}

.dash-topbar {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(320px, 0.8fr);
  gap: 12px;
  padding: 14px 16px;
}

.dash-brand h1,
.dash-panel h2,
.dash-card h2,
.dash-focus-card h3,
.dash-coach-card h3,
.dash-group-card h3,
.dash-classification-card h3,
.dash-action-item h3 {
  margin: 8px 0 0;
  font-family: "Avenir Next", "PingFang SC", "Microsoft YaHei UI", sans-serif;
  letter-spacing: 0;
}

.dash-panel h2,
.dash-card h2,
.dash-focus-card h3,
.dash-coach-card h3 {
  font-size: var(--dash-title-size);
  line-height: 1.22;
}

.dash-brand h1 {
  font-size: clamp(1.55rem, 2.4vw, 2.2rem);
}

.dash-brand p,
.dash-card__text,
.dash-family-card p,
.dash-focus-card p,
.dash-coach-card p,
.dash-timeline-item p,
.dash-action-item p,
.dash-watch-item p {
  margin: 6px 0 0;
  color: var(--ink-soft);
  font-size: var(--dash-copy-size);
  line-height: 1.42;
}

.dash-brand p {
  max-width: 720px;
  font-size: 0.92rem;
}

.dash-eyebrow,
.dash-panel__eyebrow,
.dash-label,
.dash-summary-card__label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: var(--dash-caption-size);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-soft);
}

.dash-eyebrow::before,
.dash-panel__eyebrow::before {
  content: "";
  width: 16px;
  height: 1px;
  background: rgba(29, 47, 44, 0.28);
}

.dash-topbar__side {
  display: grid;
  gap: 8px;
}

.dash-snapshot {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  background: var(--surface-strong);
}

.dash-snapshot strong,
.dash-summary-card__value,
.dash-mini-stat strong,
.dash-digest-item strong,
.dash-watch-item__rank,
.dash-table__row b,
.dash-classification-row strong {
  font-family: "Bahnschrift", "DIN Alternate", "Arial Narrow", sans-serif;
}

.dash-period-switch {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 6px;
}

.dash-period-button {
  border: 1px solid rgba(29, 47, 44, 0.1);
  background: rgba(255, 250, 244, 0.8);
  color: var(--ink-soft);
  border-radius: 999px;
  padding: 8px 10px;
  cursor: pointer;
  transition: transform 160ms ease, border-color 160ms ease, background 160ms ease;
}

.dash-period-button:hover {
  transform: translateY(-1px);
}

.dash-period-button.is-active {
  background: linear-gradient(135deg, rgba(41, 107, 115, 0.14), rgba(43, 139, 100, 0.18));
  border-color: rgba(41, 107, 115, 0.2);
  color: var(--ink);
}

.dash-summary-grid,
.dash-group-strip,
.dash-insight-grid,
.dash-board-grid,
.dash-support-grid {
  margin-top: 10px;
}

.dash-summary-grid {
  display: grid;
  grid-template-columns: var(--dash-main-columns);
  gap: 10px;
}

.dash-summary-group {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.dash-summary-card {
  position: relative;
  overflow: clip;
  padding: 10px 12px;
}

.dash-summary-card::before {
  content: "";
  position: absolute;
  inset: 0 auto auto 0;
  width: 100%;
  height: 3px;
  background: var(--tone);
}

.dash-summary-card__value {
  display: block;
  margin-top: 7px;
  font-size: var(--dash-kpi-size);
  line-height: 1.12;
}

.dash-summary-card__note {
  margin: 4px 0 0;
  color: var(--ink-soft);
  font-size: 0.7rem;
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dash-group-strip {
  display: grid;
  grid-template-columns: repeat(10, minmax(150px, 1fr));
  gap: 8px;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  padding-bottom: 0;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  mask-image: linear-gradient(90deg, #000 calc(100% - 18px), transparent);
}

.dash-group-strip::-webkit-scrollbar {
  display: none;
}

.dash-group-card {
  width: 100%;
  appearance: none;
  text-align: left;
  padding: 10px 11px;
  cursor: pointer;
  transition: transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease;
  background:
    linear-gradient(180deg, color-mix(in srgb, var(--group-soft, rgba(41, 107, 115, 0.12)) 100%, white), rgba(255, 250, 244, 0.92));
  border-color: color-mix(in srgb, var(--group-color, var(--brand)) 18%, white);
}

.dash-group-card:hover {
  transform: translateY(-1px);
}

.dash-group-card.is-active {
  border-color: color-mix(in srgb, var(--group-color, var(--brand)) 40%, white);
  background: rgba(255, 250, 244, 0.96);
  box-shadow: 0 18px 32px color-mix(in srgb, var(--group-color, var(--brand)) 18%, rgba(37, 56, 50, 0.08));
}

.dash-group-card__top,
.dash-section-heading,
.dash-panel__header,
.dash-family-card__top,
.dash-focus-card__top,
.dash-signal-item__top,
.dash-timeline-item__top,
.dash-action-item__top,
.dash-stage-row__top,
.dash-classification-row__top {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: start;
}

.dash-group-card__metrics,
.dash-meta,
.dash-chip-row,
.dash-family-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.dash-group-card h3 {
  font-size: var(--dash-section-title-size);
  color: color-mix(in srgb, var(--group-color, var(--brand)) 70%, var(--ink));
}

.dash-group-card__metrics,
.dash-meta,
.dash-family-card__meta,
.dash-digest-item span,
.dash-watch-item span,
.dash-table__row em {
  color: var(--ink-soft);
  font-size: 0.68rem;
}

.dash-status-pill,
.dash-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 999px;
  background: var(--brand-soft);
  color: var(--brand);
  font-size: 0.68rem;
  font-weight: 700;
  white-space: nowrap;
}

.dash-status-pill {
  background: var(--tone-soft, var(--warn-soft));
  color: var(--tone, var(--warn));
}

.dash-badge--ghost {
  background: rgba(41, 107, 115, 0.1);
  color: var(--brand);
}

.dash-insight-grid {
  display: grid;
  grid-template-columns: var(--dash-main-columns);
  gap: 10px;
  align-items: stretch;
  --insight-card-height: clamp(380px, 31vw, 520px);
}

.dash-insight-grid .dash-card {
  min-height: var(--insight-card-height);
  max-height: var(--insight-card-height);
  overflow: clip;
  display: flex;
  flex-direction: column;
}

.dash-insight-grid .insight-rank-list,
.dash-insight-grid .insight-table-scroll {
  min-height: 0;
  overflow-y: auto;
  padding-right: 2px;
  overscroll-behavior-y: auto;
  scrollbar-width: none;
}

.dash-insight-grid .insight-rank-list::-webkit-scrollbar,
.dash-insight-grid .insight-table-scroll::-webkit-scrollbar {
  display: none;
}

.dash-card,
.dash-panel,
.dash-focus-card,
.dash-coach-card {
  padding: 12px;
}

.dash-insight-grid .dash-card h2 {
  font-size: var(--dash-section-title-size);
}

.dash-insight-grid .dash-card__text,
.dash-watch-item__body p,
.dash-digest-item strong {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.dash-card--accent {
  background:
    linear-gradient(150deg, rgba(255, 250, 244, 0.95), rgba(246, 238, 223, 0.88)),
    linear-gradient(120deg, rgba(41, 107, 115, 0.08), rgba(203, 97, 69, 0.07));
}

.dash-mini-grid,
.dash-panel__summary,
.dash-focus-card__grid,
.dash-classification-grid {
  display: grid;
  gap: 10px;
}

.dash-mini-grid,
.dash-panel__summary,
.dash-focus-card__grid {
  margin-top: 10px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.dash-mini-stat,
.dash-digest-item {
  padding: 9px 10px;
  border-radius: 14px;
  border: 1px solid var(--line);
  background: rgba(255, 250, 244, 0.78);
}

.dash-mini-stat span,
.dash-digest-item span {
  display: block;
  color: var(--ink-soft);
  font-size: 0.78rem;
}

.dash-mini-stat strong {
  display: block;
  margin-top: 4px;
  font-size: 0.92rem;
}

.dash-digest-list,
.dash-watch-list,
.dash-family-list,
.dash-signal-list,
.dash-timeline,
.dash-action-list,
.dash-stage-list {
  display: grid;
  gap: 8px;
}

.dash-watch-item {
  width: 100%;
  appearance: none;
  border: 1px solid var(--line);
  background: rgba(255, 250, 244, 0.78);
  border-radius: 14px;
  padding: 9px 10px;
  display: flex;
  gap: 8px;
  text-align: left;
  cursor: pointer;
  transition: transform 160ms ease, border-color 160ms ease;
}

.dash-watch-item:hover {
  transform: translateY(-1px);
  border-color: rgba(41, 107, 115, 0.18);
}

.dash-watch-item__rank {
  display: inline-flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--brand-soft);
  color: var(--brand);
}

.dash-watch-item__body strong {
  display: block;
}

.dash-watch-item__body p {
  font-size: 0.72rem;
}

.dash-board-grid {
  display: grid;
  grid-template-columns: var(--dash-main-columns);
  gap: 10px;
  align-items: stretch;
  height: calc(100vh - 208px);
  min-height: 560px;
}

.dash-board-grid > .dash-panel {
  height: 100%;
  min-height: 0;
  overflow: clip;
}

.dash-board-grid > .dash-panel.dash-panel--journey {
  overflow-x: clip;
  overflow-y: clip;
}

.dash-panel {
  display: flex;
  flex-direction: column;
  min-height: min(880px, calc(100vh - 120px));
}

.dash-panel__header {
  margin-bottom: 10px;
}

.dash-table {
  margin-top: 10px;
  border: 1px solid var(--line);
  border-radius: 16px;
  overflow: clip;
}

.dash-table__head,
.dash-table__row {
  display: grid;
  grid-template-columns: 1.15fr 0.45fr 1fr 0.42fr 0.45fr 0.52fr 0.38fr;
  gap: 10px;
  align-items: center;
}

.dash-table__head {
  padding: 10px 12px;
  background: rgba(41, 107, 115, 0.08);
  color: var(--ink-soft);
  font-size: 0.68rem;
  font-weight: 700;
}

.dash-table__body {
  max-height: 760px;
  overflow: auto;
  overscroll-behavior: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}

.dash-table__row {
  width: 100%;
  appearance: none;
  border: 0;
  border-top: 1px solid rgba(29, 47, 44, 0.06);
  background: transparent;
  padding: 10px 12px;
  text-align: left;
  cursor: pointer;
  transition: background 160ms ease;
  font-size: var(--dash-data-size);
}

.dash-table__row:hover,
.dash-table__row.is-active {
  background: rgba(41, 107, 115, 0.08);
}

.dash-table__row strong,
.dash-family-card strong,
.dash-focus-card h3,
.dash-timeline-item h4,
.dash-action-item h3,
.dash-stage-row strong {
  display: block;
}

.dash-table__row em {
  display: block;
  font-style: normal;
  margin-top: 4px;
}

.dash-family-list {
  max-height: 800px;
  overflow: auto;
  overscroll-behavior: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}

.dash-family-card {
  width: 100%;
  appearance: none;
  padding: 11px;
  text-align: left;
  cursor: pointer;
  border-color: color-mix(in srgb, var(--tone, var(--brand)) 16%, white);
  transition: transform 160ms ease, background 160ms ease;
}

.dash-family-card.is-active,
.dash-family-card:hover {
  transform: translateY(-1px);
  background: rgba(255, 250, 244, 0.96);
}

.dash-panel--journey {
  height: 100%;
  min-height: auto;
  overflow: clip;
}

.journey-panel__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior-y: auto;
  padding-right: 0;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}

.journey-panel__body::-webkit-scrollbar {
  display: none;
}

.dash-focus-card,
.dash-coach-card {
  margin-bottom: 8px;
}

.dash-signal-item,
.dash-classification-row {
  padding: 10px;
  border-radius: 14px;
  border: 1px solid var(--line);
  background: rgba(255, 250, 244, 0.76);
}

.dash-signal-item__bar,
.dash-classification-row__bar,
.dash-stage-row__bar {
  margin-top: 10px;
  height: 7px;
  border-radius: 999px;
  background: rgba(29, 47, 44, 0.08);
  overflow: hidden;
}

.dash-signal-item__bar span,
.dash-classification-row__bar span,
.dash-stage-row__bar span {
  display: block;
  width: var(--level, 50%);
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, var(--tone, var(--brand)), color-mix(in srgb, var(--tone, var(--brand)) 60%, white));
}

.dash-signal-item span,
.dash-classification-row span,
.dash-stage-row span {
  color: var(--ink-soft);
  font-size: 0.8rem;
}

.dash-timeline {
  position: relative;
  padding-left: 16px;
  max-height: 420px;
  overflow-y: auto;
  gap: 2px;
}

.dash-timeline::before {
  content: "";
  position: absolute;
  left: 7px;
  top: 8px;
  bottom: 8px;
  width: 1px;
  background: linear-gradient(180deg, rgba(29, 47, 44, 0.22), rgba(29, 47, 44, 0.02));
}

.dash-timeline-item {
  position: relative;
  padding: 4px 6px 4px 16px;
}

.dash-timeline-item::before {
  content: "";
  position: absolute;
  left: -12px;
  top: 12px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: white;
  border: 2px solid var(--tone, var(--brand));
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--tone, var(--brand)) 14%, white);
}

.dash-timeline-item__time {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(255, 250, 244, 0.88);
  color: var(--ink-soft);
  font-size: 0.76rem;
  font-weight: 700;
}

.dash-support-grid {
  display: grid;
  grid-template-columns: var(--dash-main-columns);
  gap: 10px;
}

.dash-classification-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.dash-classification-card {
  padding: 12px;
}

.dash-classification-list {
  display: grid;
  gap: 10px;
  margin-top: 12px;
}

.dash-action-list {
  display: grid;
  gap: 10px;
}

.dash-action-item {
  padding: 12px;
}

.dash-stage-row {
  padding: 12px;
}

.dash-table__body::-webkit-scrollbar,
.dash-family-list::-webkit-scrollbar {
  display: none;
}

@media (max-width: 1360px) {
  .dash-group-strip,
  .dash-insight-grid,
  .dash-board-grid,
  .dash-support-grid,
  .dash-classification-grid {
    grid-template-columns: 1fr;
  }

  .dash-panel {
    min-height: auto;
  }

  .dash-board-grid {
    height: auto;
    min-height: 0;
  }

  .dash-insight-grid .dash-card {
    min-height: auto;
    max-height: none;
  }

  .dash-board-grid > .dash-panel {
    height: auto;
    max-height: none;
    overflow: visible;
  }
}

@media (max-width: 860px) {
  .dash-shell {
    padding: 14px 12px 20px;
  }

  .dash-topbar,
  .dash-mini-grid,
  .dash-panel__summary,
  .dash-focus-card__grid,
  .dash-period-switch {
    grid-template-columns: 1fr;
  }

  .dash-summary-grid {
    grid-template-columns: 1fr;
  }

  .dash-summary-group {
    grid-template-columns: 1fr;
  }

  .dash-topbar,
  .dash-card,
  .dash-panel,
  .dash-focus-card,
  .dash-coach-card {
    padding: 14px;
  }

  .dash-topbar,
  .dash-group-card__top,
  .dash-section-heading,
  .dash-panel__header,
  .dash-family-card__top,
  .dash-focus-card__top,
  .dash-signal-item__top,
  .dash-timeline-item__top,
  .dash-action-item__top,
  .dash-stage-row__top,
  .dash-classification-row__top,
  .dash-snapshot {
    flex-direction: column;
    align-items: start;
  }

  .dash-table__head,
  .dash-table__row {
    grid-template-columns: 1fr;
  }
}

.dash-call-list,
.dash-form-grid,
.dash-suggestion-list,
.dash-violation-list {
  display: grid;
  gap: 8px;
}

.dash-call-item,
.dash-form-item,
.dash-suggestion-item,
.dash-violation-item {
  padding: 12px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  background: var(--surface);
}

.dash-call-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dash-call-item button,
.dash-form-item button {
  padding: 6px 12px;
  background: var(--brand);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
}

.dash-call-item button:hover,
.dash-form-item button:hover {
  background: var(--brand-strong);
}

.dash-form-item h4,
.dash-suggestion-item h4,
.dash-violation-item h4 {
  margin: 0 0 8px 0;
  font-size: 0.9rem;
}

.dash-violation-item p {
  margin: 4px 0;
  font-size: 0.8rem;
  color: var(--ink-soft);
}

.dash-table--interaction .dash-table__head,
.dash-table--interaction .dash-table__row {
  grid-template-columns: 1.2fr 0.8fr 0.8fr 0.6fr 0.6fr;
}
</style>
